'use client';

import type { JSX, RefObject } from 'react';
import React, { forwardRef, memo } from 'react';
import { Spinner } from 'components/Spinner';
import { AnimatePresence, motion } from 'framer-motion';
import styles from '../Collections.module.css';
import { type FocusedItem } from '../hooks/useSetFocusedKey';
import { ListStateContextProvider } from './components/ListStateContext';
import { ListBox } from './ListBox';
import type { ListProps } from './types';
import { useFilteredListState } from './useFilteredListState';

type Props<T> = ListProps<T> & {
  search?: React.ReactNode;
  scrollToKey?: FocusedItem;
};

function List<T extends object>(
  { search, variant = 'list', ...props }: Props<T>,
  forwardedRef: React.Ref<HTMLDivElement>
) {
  const { state } = useFilteredListState({
    ...props,
    variant,
  });

  return (
    <ListStateContextProvider value={state}>
      <div
        className={styles.listWithSearch}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.stopPropagation();
          }
        }}
      >
        <AnimatePresence initial={false}>
          {search && (
            <motion.div
              animate={{ height: 'auto', opacity: 1, overflow: 'hidden' }}
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              transition={{
                duration: 0.2,
              }}
            >
              <motion.div
                animate={{ transform: 'translateY(0)' }}
                exit={{ transform: 'translateY(-8px)' }}
                initial={{
                  transform: 'translateY(-8px)',
                }}
                transition={{
                  duration: 0.2,
                }}
              >
                {search}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <div
          className={styles.listContent}
          data-loading={state.isLoadingVisible || undefined}
        >
          {!state.isLoadingVisible && (
            <ListBox
              ref={forwardedRef}
              state={state}
              variant={variant}
              {...props}
            />
          )}
        </div>
        {state.isLoadingVisible && (
          <Spinner aria-label="Loading" centered delay={0} isLoading />
        )}
      </div>
    </ListStateContextProvider>
  );
}

const _List = memo(forwardRef(List)) as <T>(
  props: Props<T> & { ref?: RefObject<any> }
) => JSX.Element;
export { _List as List };
